/**
 * L4-JUICE-006: Impact Particle Pool
 *
 * Object pool for impact particles.
 * Reduces GC pressure by reusing particle objects.
 */

import { ImpactParticle, ImpactParticleRenderer, IMPACT_EFFECT_MAP } from './impact-particle-renderer';

/**
 * Pool statistics
 */
export interface PoolStatistics {
  total: number;
  active: number;
  available: number;
  recycled: number;
}

/**
 * ImpactParticlePool
 *
 * Object pool for impact particles.
 * Reduces GC pressure by reusing particle objects.
 */
export class ImpactParticlePool {
  private renderer: ImpactParticleRenderer;
  private pool: ImpactParticle[] = [];
  private active: ImpactParticle[] = [];
  private recycledCount: number = 0;

  private static readonly MAX_POOL_SIZE = 50;
  private static readonly INITIAL_POOL_SIZE = 10;

  constructor(renderer: ImpactParticleRenderer) {
    this.renderer = renderer;
  }

  /**
   * Acquire particle from pool
   *
   * @param damageType - Type of damage for effect selection
   * @param position - World position
   * @returns Impact particle ready for use
   */
  public async acquire(
    damageType: string,
    position: { x: number; y: number }
  ): Promise<ImpactParticle> {
    // Try to reuse from pool
    let particle = this.pool.pop();

    if (particle) {
      // Reset particle for reuse
      particle.container.x = position.x;
      particle.container.y = position.y;
      particle.container.alpha = 1.0;
      particle.startTime = Date.now();
      particle.isComplete = false;
      particle.sprite.gotoAndPlay(0);

      this.recycledCount++;
    } else {
      // Create new particle
      const effectId = this.getEffectId(damageType);
      particle = await this.renderer.createParticle(effectId, position);
    }

    this.active.push(particle);
    return particle;
  }

  /**
   * Release particle back to pool
   *
   * @param particle - Particle to release
   */
  public release(particle: ImpactParticle): void {
    // Remove from active list
    const index = this.active.indexOf(particle);
    if (index !== -1) {
      this.active.splice(index, 1);
    }

    // Return to pool if not at capacity
    if (this.pool.length < ImpactParticlePool.MAX_POOL_SIZE) {
      this.pool.push(particle);
    } else {
      // Destroy excess particles
      this.renderer.destroyParticle(particle);
    }
  }

  /**
   * Update all active particles
   *
   * @param currentTime - Current timestamp
   * @returns Particles that completed this frame
   */
  public update(currentTime: number): ImpactParticle[] {
    const completed: ImpactParticle[] = [];

    for (const particle of this.active) {
      this.renderer.updateAnimation(particle, currentTime);

      if (particle.isComplete) {
        completed.push(particle);
      }
    }

    return completed;
  }

  /**
   * Get pool statistics
   */
  public getStatistics(): PoolStatistics {
    return {
      total: this.pool.length + this.active.length,
      active: this.active.length,
      available: this.pool.length,
      recycled: this.recycledCount
    };
  }

  /**
   * Clear all particles
   */
  public clear(): void {
    // Destroy active particles
    for (const particle of this.active) {
      this.renderer.destroyParticle(particle);
    }

    // Destroy pooled particles
    for (const particle of this.pool) {
      this.renderer.destroyParticle(particle);
    }

    this.active = [];
    this.pool = [];
  }

  /**
   * Destroy pool and cleanup
   */
  public destroy(): void {
    this.clear();
    this.recycledCount = 0;
  }

  /**
   * Get effect ID for damage type
   */
  private getEffectId(damageType: string): string {
    return IMPACT_EFFECT_MAP[damageType] || IMPACT_EFFECT_MAP.physical;
  }
}
